import { gql, TypedDocumentNode } from '@apollo/client';
import {
  AutonomousMonitorLocation,
  MonitorHourlyFreqData,
  MonitorOutreachHourlyStat,
  UnresolvedLocationListItem,
} from '../types';

export const getDailyMonitorDataQuery = (
  monitorId: string,
  startTime: string,
  endTime: string,
): any => {
  return gql`
  query rule_evaluation{
    rule_evaluation_result: signal_3_p_evaluate_monitor_daily(
      args: {
        p_monitor_id: "${monitorId}",
        p_start_time: "${startTime}",
        p_end_time: "${endTime}"
      },
      order_by: {ts: desc}
    ) {
      ts
      location_count
    }
  }
  `;
};

export const getHourlyMonitorDataQuery = (
  monitorId: string,
  startTime: string,
  endTime: string,
): any => {
  return gql`
  query rule_evaluation{
    rule_evaluation_result: signal_3_p_evaluate_monitor_hourly(
      args: {
        p_monitor_id: "${monitorId}",
        p_start_time: "${startTime}",
        p_end_time: "${endTime}"
      },
      order_by: {ts: desc}
    ) {
      ts
      location_count
    }
  }
  `;
};

export const getMonitorDataLatestQuery = (
  monitorId: string,
): TypedDocumentNode<{
  signal_3_p_evaluate_monitor_freq_aggregate: {
    aggregate: {
      count: number;
    };
  };
}> => {
  return gql`
  query latest_location_count {
  signal_3_p_evaluate_monitor_freq_aggregate(args: {p_monitor_id: "${monitorId}"}) {
    aggregate {
      count
    }
  }
  }`;
};

export const getMonitorHourlyFreq = (
  monitorId: string,
  startTime: string,
  endTime: string,
): TypedDocumentNode<{
  signal_3_p_evaluate_monitor_hourly_freq: MonitorHourlyFreqData[];
}> => {
  return gql`
    query signal_3_p_evaluate_monitor_hourly_freq {
      signal_3_p_evaluate_monitor_hourly_freq(
        args: {
          p_monitor_id: "${monitorId}"
          p_start_time: "${startTime}"
          p_end_time: "${endTime}"
        }
        order_by: { ts: desc }
      ) {
        ts
        location_count
        average_health_score
        new_location_count_7d
      }
    }
  `;
};

type GetUnresolvedLocationsMonitorArgs = {
  monitorId: string;
  limit?: number;
  offset?: number;
  isDaily?: boolean;
  timestamp?: string;
};

export const getUnresolvedLocationsMonitor = ({
  monitorId,
  limit = 10,
  offset = 0,
  isDaily,
  timestamp,
}: GetUnresolvedLocationsMonitorArgs): TypedDocumentNode<{
  signal_3_p_evaluate_monitor_freq: UnresolvedLocationListItem[];
}> => gql`
  query signal_3_p_evaluate_monitor_freq {
    signal_3_p_evaluate_monitor_freq(
      args: { p_monitor_id: "${monitorId}"
        ${isDaily ? `, is_daily: true` : ``}
      ${timestamp ? `, p_ts: "${timestamp}"` : ``}
      }
      order_by: { health_score: desc }
      limit: ${limit}
      offset: ${offset}
    ) {
      customer_id
      location_id
      account_id
      health_score
      ts
    }
  }
`;

export const getMonitorOutreachStatsFreq = (
  monitorId: string,
  partnerId: string,
  daily: boolean,
): TypedDocumentNode<{
  outreachStats: MonitorOutreachHourlyStat[];
}> => gql`
query signal_3_p_monitor_outreach_${daily ? 'daily' : 'hourly'
  }_stats{ outreachStats: signal_3_p_monitor_outreach_${daily ? 'daily' : 'hourly'
  }_stats(
      args: {
      p_monitor_id: "${monitorId}"
      }, where: { partner_id: { _eq: "${partnerId}" } }) {
    ts
    support_start_count
    support_clear_count
    support_block_count
    subscriber_open_count
    subscriber_receive_count
    subscriber_send_count
    subscriber_block_count
    subscriber_bounce_count
    subscriber_error_count
    webhook_count
    webhook_error_count
      }}`;

export const getTriggeredOutreachEvents = (monitorId: string): any => {
  return gql`
  query outreach_events {
    outreachEvents: signal_3_p_monitor_outreach_events(
      args: { p_monitor_id: "${monitorId}" },
      order_by: {created_at: desc}
      ) {
        event_id
        created_at
        cleared_at
        sent_at
        total_count
        active_count
        target
        push_sent_count
        push_received_count
        push_clicked_count
        subscriber_block_count
        subscriber_error_count
        webhook_count
        webhook_error_count
    }
  }`;
};

export const getMapTriggeredOutreachEvents = (monitorId: string): any => {
  return gql`
  query latest_monitor_location_stats {
    outreachEvents: signal_3_p_evaluate_monitor_freq(
      args: {p_monitor_id: "${monitorId}" }
      ) {
        location_id
        customer_id
        latitude
        longitude
    }
  }`;
};

export function getSubscriberStatsQuery(
  fieldName: string,
  partnerId: string,
  supportOutreachId: string,
  days: number = 90,
): string {
  return ` ${fieldName}: signal_3_outreach_stats_daily_fact(
    where: {
      partner_id: {_eq: "${partnerId}"},
      outreach_id: {_eq: "${supportOutreachId}"}
    },
    order_by: {ts: desc},
    limit: ${days}
    ) {
      ts
      start_count
      clear_count
    }`;
}

export const getMonitorOutreachStats = (
  monitorId: string,
  partnerId: string,
  days: number,
): any => {
  let daily: boolean;
  let daysCount: number = days;
  let hoursCount: number = days * 24;

  if (days === 1 || days === 3) {
    daily = false;
  } else {
    daily = true;
  }

  return gql`
    query signal_3_outreach_stats {
      stats: signal_3_p_monitor_outreach_${daily ? 'daily_stats' : 'hourly_stats'
    }(
          args: {
              p_monitor_id: "${monitorId}",
              p_limit: ${daily ? daysCount : hoursCount}
          },
          where: {
              partner_id: {_eq: "${partnerId}"}
          },
      ) {
          ts
          subscriber_block_count
          subscriber_bounce_count
          subscriber_error_count
          support_start_count
          support_clear_count
          support_block_count
          subscriber_open_count
          subscriber_receive_count
          subscriber_send_count
          webhook_count
          webhook_error_count
      }
    }
  `;
};

export const getLocationsDataForDownload = (
  eventId: string,
  monitorId: string,
  needAccountId: boolean,
) => {
  return gql`
    query outreach_locations {
      locations: signal_3_p_outreach_event_locations(
        args: {
          p_event_id: "${eventId}" ,
          p_monitor_id: "${monitorId}"
        },
        order_by: {created_at: desc}
      ) {
        location_id
        customer_id
        ${needAccountId ? 'account_id' : ''}
        created_at
        cleared_at
        sent_at
      }
    }
  `;
};

export const getOutreachEventLocations = (
  eventId: string,
  limit: number,
  page: number,
  monitorId: string,
) => {
  return gql`
  query outreach_locations {
    locations: signal_3_p_outreach_event_locations(
    args: {
        p_event_id: "${eventId}",
        p_monitor_id: "${monitorId}"
      },
      order_by: {created_at: desc},
      limit: ${limit},
      offset: ${limit * page}
    ) {
      location_id
      customer_id
      created_at
      cleared_at
      sent_at
    }
  }`;
};

// Autonomous Monitors

export const getAutonomousMonitorLatestGraph = (
  monitorId: string,
): TypedDocumentNode<{
  latest_data: {
    yellow: {
      ts: string;
      location_count: number;
    }[];
    red: {
      ts: string;
      location_count: number;
    }[];
  };
}> => gql`
    query signal_3_p_evaluate_auto_monitor_latest {
      latest_data: signal_3_p_evaluate_auto_monitor_latest(
        args: { p_monitor_id: "${monitorId}" }
      ) {
        yellow {
          ts
          location_count
        }
        red {
          ts
          location_count
        }
      }
    }
  `;

export const getAutonomousMonitorOutreachEvents = (
  monitorId: string,
): TypedDocumentNode<{
  events: {
    monitorId: string;
    event_type: 'warning' | 'alert';
    start_time: string;
    end_time?: string;
  }[];
}> => gql`
query signal_3_auto_monitor_outreach_events {
  events: signal_3_auto_monitor_outreach_events(
                args: { p_monitor_id: "${monitorId}" }
              ) {
    monitor_id
    event_type
    start_time
    end_time
  }
}
`;

export const getAutonomousMonitorHourlyFreq = (
  monitorId: string,
  startTime: string,
  endTime: string,
): TypedDocumentNode<{
  points: {
    yellow: {
      ts: string;
      location_count: number;
    }[];
    red: {
      ts: string;
      location_count: number;
    }[];
  };
}> => gql`
  query signal_3_p_evaluate_auto_monitor_hourly_freq {
    points: signal_3_p_evaluate_auto_monitor_hourly_freq(
      args: {
        p_monitor_id: "${monitorId}"
        p_start_time: "${startTime}"
        p_end_time: "${endTime}"
      }
      order_by: { ts: desc }
    ) {
      yellow {
        ts
        location_count
      }
      red {
        ts
        location_count
      }
    }
  }
`;

export const getAutonomousMonitorLocations = (
  monitorId: string,
  limit?: number,
  offset?: number,
): TypedDocumentNode<{
  locations: {
    yellow: AutonomousMonitorLocation[];
    red: AutonomousMonitorLocation[];
  };
}> => gql`
  query signal_3_p_evaluate_auto_monitor_freq {
    locations: signal_3_p_evaluate_auto_monitor_freq(
      args: { p_monitor_id: "${monitorId}" }
      order_by: { location_id: desc, health_score: desc }
      ${limit ? `limit: ${limit}` : ''}
      ${offset ? `offset: ${offset}` : ''}
    ) {
      yellow {
        location_id
        account_id
        health_score
        ts
        latitude
        longitude
      }
      red {
        location_id
        account_id
        health_score
        ts
        latitude
        longitude
      }
    }
  }
`;
